import { useEffect, useMemo, useState } from "react";
import { BarChart3, TrendingUp, TrendingDown, Wheat, Droplets, Milk, Beef, Users, CircleDollarSign, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from "recharts";
import { Link } from "react-router-dom";
import { api, money } from "../lib/api";
import { PageHeader, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

const MODULE_META = {
  rice: { label: "Çeltik", icon: Wheat, to: "/rice" },
  dairy: { label: "Süt", icon: Droplets, to: "/dairy" },
  cattle: { label: "Büyükbaş", icon: Milk, to: "/cattle" },
  sheep: { label: "Küçükbaş", icon: Beef, to: "/sheep" },
  employees: { label: "Çalışan", icon: Users, to: "/employees" },
  finance: { label: "Kasa", icon: CircleDollarSign, to: "/finance" },
};

export default function Reports() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("monthly");

  useEffect(() => {
    api.get("/reports").then(setData).catch(() => setError("Rapor yüklenemedi."));
  }, []);

  const chartData = useMemo(() => {
    if (!data) return [];
    if (period === "monthly") {
      return data.monthly.map((m) => ({ label: m.label, Gelir: m.income, Gider: m.expense, Net: m.net }));
    }
    return data.yearly.map((y) => ({ label: y.year, Gelir: y.income, Gider: y.expense, Net: y.net }));
  }, [data, period]);

  const empty = !data || data.event_count === 0;
  const totals = data?.totals || { income: 0, expense: 0, net: 0 };
  const modules = data ? Object.entries(data.by_module) : [];
  modules.sort(([, a], [, b]) => (b.income + b.expense) - (a.income + a.expense));

  return (
    <>
      <PageHeader
        eyebrow="RAPORLAR · DÖNEMSEL ÖZET"
        title="Raporlar"
        subtitle="Aylık ve yıllık gelir-gider grafikleri, modül bazlı karşılaştırma."
        heroImage={HERO.reports}
        heroTint={HERO_TINT.reports}
      />

      {error && <div className="api-error" data-testid="reports-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Toplam gelir" value={money(totals.income)} tone="green" testId="reports-stat-income" />
        <StatMini label="Toplam gider" value={money(totals.expense)} tone="red" testId="reports-stat-expense" />
        <StatMini label="Net" value={money(totals.net)} tone="teal" testId="reports-stat-net" />
        <StatMini label="Hareket sayısı" value={`${data?.event_count || 0}`} tone="orange" testId="reports-stat-events" />
      </section>

      <div className="filter-row">
        <button className={`chip ${period === "monthly" ? "active" : ""}`} data-testid="period-monthly" onClick={() => setPeriod("monthly")}>Son 12 Ay</button>
        <button className={`chip ${period === "yearly" ? "active" : ""}`} data-testid="period-yearly" onClick={() => setPeriod("yearly")}>Yıllık</button>
      </div>

      <div className="panel report-chart-panel">
        <div className="panel-head">
          <div>
            <h2>{period === "monthly" ? "Son 12 ay gelir & gider" : "Yıllık toplamlar"}</h2>
            <p>Karşılaştırmalı bar grafik + net eğrisi</p>
          </div>
        </div>
        {empty ? (
          <div className="empty-state" style={{ padding: "60px 0" }}>
            <BarChart3 size={30} />
            <b>Henüz veri yok</b>
            <p>Kayıt eklemeye başlayın; grafikler otomatik dolacak.</p>
          </div>
        ) : (
          <div style={{ width: "100%", height: 340 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2ea" />
                <XAxis dataKey="label" stroke="#8b9b88" fontSize={11} />
                <YAxis stroke="#8b9b88" fontSize={11} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip
                  formatter={(v) => money(v)}
                  contentStyle={{ background: "#fff", border: "1px solid #e2e8df", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#1e293b", fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Gelir" fill="#2d5a27" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Gider" fill="#d97706" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {!empty && (
        <div className="panel report-chart-panel" style={{ marginTop: 20 }}>
          <div className="panel-head">
            <div>
              <h2>Net kâr eğrisi</h2>
              <p>Dönem bazlı net durum trendi</p>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2ea" />
                <XAxis dataKey="label" stroke="#8b9b88" fontSize={11} />
                <YAxis stroke="#8b9b88" fontSize={11} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#fff", border: "1px solid #e2e8df", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="Net" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 4, fill: "#0d9488" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="panel" style={{ marginTop: 20 }}>
        <div className="panel-head">
          <div>
            <h2>Modül bazlı özet</h2>
            <p>Her modülün gelir, gider ve net katkısı</p>
          </div>
        </div>
        {modules.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: 12, padding: "20px 0" }}>Henüz modül hareketi yok.</p>
        ) : (
          <div className="report-modules">
            {modules.map(([mod, vals]) => {
              const meta = MODULE_META[mod] || { label: mod, icon: BarChart3, to: "/" };
              const Icon = meta.icon;
              return (
                <Link to={meta.to} className="report-module-row" key={mod} data-testid={`report-module-${mod}`}>
                  <div className="report-module-icon"><Icon size={18} /></div>
                  <div className="report-module-body">
                    <b>{meta.label}</b>
                    <div className="report-module-bars">
                      <span className="rm-income"><TrendingUp size={11} /> {money(vals.income)}</span>
                      <span className="rm-expense"><TrendingDown size={11} /> {money(vals.expense)}</span>
                    </div>
                  </div>
                  <div className="report-module-net">
                    <small>Net</small>
                    <strong className={vals.net >= 0 ? "positive" : "negative"}>{money(vals.net)}</strong>
                  </div>
                  <ArrowRight size={16} className="report-module-chev" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
